import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getBatchByUID } from '@selectors/batchGroups';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import PreviewButton from '@comp/PreviewButton/PreviewButton';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import { getIntroVideoURL } from '@utils/textHelper';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import { segment } from '@actions/analytics';
import {
  PLATFORM_BATCH_PAGE,
  PLATFORM_BATCH_VIDEO_PREVIEW_CLICKED
} from '@constants/segment';
import Image from '@base/Image';
import H7 from '@base/H7/H7';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { getDarkModeImage } from '@cont/BatchGroup/utils';
import { PLAY_ICON, PLAY_ICON_DARK } from '@cont/BatchGroup/constants';

const PlayButton = styled(PreviewButton)`
  width: 117px;
  height: 40px;
  background: var(--color-base-1);
  backdrop-filter: blur(${Spacings.SPACING_1B});
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_10B};
  &:hover {
    background: var(--color-base-1);
  }
  ${mediaQuery.uptoMobile} {
    height: ${Spacings.SPACING_7B};
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
    border-radius: ${Spacings.SPACING_7B};
    width: auto;
  }
`;

const Intro = styled.iframe`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: ${Spacings.SPACING_2B};
  margin-right: ${Spacings.SPACING_8B};
  background-color: var(--color-base-fill);
  transition: all 300ms ease;
  margin: auto ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    height: 186px;
    width: 100%;
  }
`;

const Dummy = styled.div`
  position: relative;
  margin: auto ${Spacings.SPACING_0B};
`;

const CourseThumbnail = styled(Thumbnail)`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  cursor: ${({ preview }) => {
    return preview ? 'pointer' : 'unset';
  }};
  height: ${({ height }) => height};
  border-radius: ${Spacings.SPACING_2B};
  transition: all 300ms ease-in-out;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-right: ${Spacings.SPACING_0B};
    height: 186px;
  }
`;

const Preview = styled(H7)`
  margin-left: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.SMALL};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;

const BatchThumbnail = ({ batchUID, height, width }) => {
  const batch = useSelectorToStore(getBatchByUID(batchUID)) || {};
  const { preview, thumbnail } = batch;
  const [showPreview, setShowPreview] = useState(false);
  const segmentData = usePlatformEventProps({
    batch,
    lastPrimarySourceSection: `${PLATFORM_BATCH_PAGE} - Page Header`,
    isBatch: true
  });
  const isDarkMode = useDarkModeStatus(getIsDarkModeOn);
  const playIcon = useMemo(
    () => getDarkModeImage(isDarkMode, PLAY_ICON_DARK, PLAY_ICON),
    [isDarkMode]
  );
  const showIntro = () => {
    if (preview) {
      setShowPreview(true);
      segment.track(PLATFORM_BATCH_VIDEO_PREVIEW_CLICKED, segmentData);
    }
  };

  if (showPreview)
    return (
      <Intro
        $height={height}
        $width={width}
        title="intro"
        src={getIntroVideoURL(preview)}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen="allowFullScreen"
      />
    );
  return (
    <Dummy>
      <CourseThumbnail
        onClick={showIntro}
        height={height}
        width={width}
        imgUrl={thumbnail}
        preview={preview}
      >
        {preview && (
          <PlayButton onClick={showIntro}>
            <Image src={playIcon} alt="play" width={14} height={14} />
            <Preview>PREVIEW</Preview>
          </PlayButton>
        )}
      </CourseThumbnail>
    </Dummy>
  );
};

export default BatchThumbnail;

BatchThumbnail.propTypes = {
  batchUID: PropTypes.string.isRequired,
  height: PropTypes.string,
  width: PropTypes.string
};

BatchThumbnail.defaultProps = {
  height: '316px',
  width: '560px'
};
